import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/privacy.scss";

function Privacy() {
  const email = siteConfig.contact?.email;

  useEffect(() => {
    document.title = `Privacy — ${siteConfig.brandName}`;
  }, []);

  return (
    <main className="policy-page policy-page--privacy" id="main-content">
      <header className="policy-hero">
        <p className="policy-eyebrow">The thoughtful fine print</p>
        <h1>Privacy, in plain English.</h1>
        <p>
          We value the trust behind every order and every note. This policy
          explains what information {siteConfig.brandName} collects and how we
          keep it safe.
        </p>
        <span>Last updated August 14, 2026</span>
      </header>

      <div className="policy-layout">
        <aside className="policy-nav" aria-label="Privacy policy sections">
          <p>On this page</p>
          <a href="#privacy-collect">Information we collect</a>
          <a href="#privacy-use">How we use it</a>
          <a href="#privacy-sharing">When we share it</a>
          <a href="#privacy-choices">Your choices</a>
          <a href="#privacy-security">Security and retention</a>
          <a href="#privacy-contact">Contact us</a>
        </aside>

        <article className="policy-content">
          <section className="policy-lead">
            <h2>A short version</h2>
            <p>
              We collect only the information needed to run our shop, fulfill
              orders, improve your experience and communicate when you ask us
              to. We do not sell your personal information.
            </p>
          </section>

          <section id="privacy-collect">
            <h2>Information we collect</h2>
            <p>Depending on how you use the site, we may collect:</p>
            <ul>
              <li>Contact details such as your name, email, phone and delivery address.</li>
              <li>Order information, including products purchased, gift notes and order history.</li>
              <li>Payment confirmation from our payment processor. We do not store full card numbers.</li>
              <li>Messages you send through our contact form or customer-care channels.</li>
              <li>Technical data such as browser type, device, approximate location and site activity.</li>
            </ul>
          </section>

          <section id="privacy-use">
            <h2>How we use information</h2>
            <p>We use personal information to:</p>
            <ul>
              <li>Process, ship and support your orders.</li>
              <li>Send service messages, including receipts and delivery updates.</li>
              <li>Respond to questions and resolve returns or damaged-item claims.</li>
              <li>Prevent fraud, protect our services and comply with legal obligations.</li>
              <li>Improve our products, website and customer experience.</li>
              <li>Send marketing only when you have chosen to receive it.</li>
            </ul>
          </section>

          <section>
            <h2>Cookies and similar tools</h2>
            <p>
              Cookies keep your cart working, remember preferences and help us
              understand which parts of the shop are useful. You can limit
              cookies in your browser, though some checkout features may no
              longer work as intended.
            </p>
          </section>

          <section id="privacy-sharing">
            <h2>When we share information</h2>
            <p>
              We share only what is necessary with trusted providers that help
              us operate—such as payment processors, fulfillment partners,
              carriers, email services and analytics providers. They may use
              the data only to perform services for us. We may also disclose
              information when required by law or during a business transfer.
            </p>
          </section>

          <section id="privacy-choices">
            <h2>Your choices and rights</h2>
            <p>
              Depending on where you live, you may ask to access, correct,
              delete or receive a copy of your personal information. You may
              unsubscribe from marketing at any time using the link in an
              email; essential order messages will still be sent.
            </p>
          </section>

          <section id="privacy-security">
            <h2>Security and retention</h2>
            <p>
              We use reasonable administrative and technical safeguards to
              protect personal information. No online system is risk-free, so
              we keep information only as long as needed for the purposes
              described here, including tax, accounting and legal requirements.
            </p>
          </section>

          <section>
            <h2>Children and international visitors</h2>
            <p>
              Our shop is not directed to children under 13. If you believe a
              child submitted information, please contact us. Information may
              be processed in countries other than your own, with protections
              appropriate to applicable law.
            </p>
          </section>

          <section>
            <h2>Updates to this policy</h2>
            <p>
              We may update this notice as our shop or legal obligations
              change. The date above shows the latest revision. Continued use
              of the site after an update means the revised policy applies.
            </p>
          </section>

          <section className="policy-contact" id="privacy-contact">
            <p className="policy-eyebrow">Still wondering?</p>
            <h2>Ask us anything about your data.</h2>
            <p>
              Use our <Link to="/contact">contact form</Link>
              {email && <> or email <a href={`mailto:${email}`}>{email}</a></>}.
            </p>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Privacy;
